import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
} from "axios";
import { getApiBaseUrl } from "@/utils/apiUtils";
import { t } from "@/i18n";
import { getToken } from "@/utils/authToken";
import { getLanguage } from "@/i18n";

interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
}

interface HttpClientConfig extends AxiosRequestConfig {
  skipCache?: boolean;
  skipCompanyFilter?: boolean;
  cacheTtl?: number;
}

/**
 * UnifiedHttpClient — centralized HTTP client that supports:
 * 1. Token-based auth (Authorization: token api_key:api_secret, injected per request)
 * 2. In-memory cache for GET (5min default TTL)
 * 3. Centralized 401/403 handling with cascade logout
 */
class UnifiedHttpClient {
  private axiosInstance: AxiosInstance | null = null;
  private cacheMap: Map<string, CacheEntry> = new Map();
  private isRetrying = false;
  private isLoggingOut = false;
  private readonly CACHE_TTL_DEFAULT = 5 * 60 * 1000;

  /**
   * Endpoints that must NEVER use cache.
   * Device State stores only the latest value and changes every second.
   * Alerts/Contacts can be modified at any moment and must stay fresh.
   */
  private readonly ENDPOINTS_NO_CACHE = [
    "/api/resource/Device%20State",
    "/api/resource/Device%20Alerts",
    "/api/resource/IoT%20Contact%20Group",
    "/api/resource/IoT%20Personal%20Contact",
  ];

  private initializeAxios(): void {
    if (this.axiosInstance) return;

    this.axiosInstance = axios.create({
      baseURL: getApiBaseUrl(),
      timeout: 60000,
      withCredentials: false,
      headers: {
        Accept: "application/json",
      },
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.axiosInstance!.interceptors.request.use(
      (config) => {
        // Frappe doesn't support HTTP 100-continue; strip Expect to avoid 417.
        if (config.headers) {
          delete config.headers["Expect"];
          // Token-based auth: inject Authorization: token api_key:api_secret.
          const token = getToken();
          if (token) {
            config.headers["Authorization"] = `token ${token}`;
          }
        }

        // Idioma da tela para o backend responder no mesmo idioma (mensagens de
        // `frappe.throw` e `_server_messages`). Para usuário logado o Frappe
        // ignora Accept-Language e só olha `_lang` no form_dict — daí o param
        // na query, que vale tanto para GET quanto para POST. Deliberadamente
        // NÃO gravamos User.language: a escolha vale para esta tela, não muda a
        // preferência do usuário no Desk.
        config.params = { ...(config.params ?? {}), _lang: getLanguage() };

        return config;
      },
      (error) => {
        console.error("❌ [UNIFIED_HTTP] Request Error:", error);
        return Promise.reject(error);
      },
    );

    this.axiosInstance!.interceptors.response.use(
      (response) => response,
      async (error) => {
        const isCanceledError =
          error instanceof Error &&
          (error.message === "canceled" ||
            error.name === "CanceledError" ||
            (error as any).code === "ERR_CANCELED");

        if (isCanceledError) {
          return Promise.reject(error);
        }

        // Frappe occasionally returns 202 on DELETE after CORS/preflight noise.
        const isDeleteMethod = error.config?.method?.toLowerCase() === "delete";
        const hasSuccessResponse =
          error.response?.status === 202 || error.response?.status === 200;
        if (isDeleteMethod && hasSuccessResponse) {
          return error.response;
        }

        if (error.response?.status === 401 && !this.isRetrying) {
          return this.handle401Error(error);
        }

        if (error.response?.status === 403) {
          return this.handle403Error(error);
        }

        console.error(
          `❌ [UNIFIED_HTTP] ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
          {
            status: error.response?.status,
            message: error.response?.data?.message || error.message,
          },
        );

        return Promise.reject(error);
      },
    );
  }

  private async handle401Error(error: any): Promise<any> {
    console.warn(
      "[UNIFIED_HTTP] 401 Unauthorized — token inválido/expirado",
    );

    if (this.isLoggingOut) {
      return Promise.reject(error);
    }

    this.isRetrying = true;
    this.isLoggingOut = true;

    try {
      sessionStorage.setItem(
        "login_error_message",
        "Sua sessão expirou. Por favor, faça login novamente.",
      );

      // Lazy imports to avoid circular deps between httpClient ↔ stores/hooks.
      const [{ useAuthStore }, { triggerSessionExpired }] = await Promise.all([
        import("@/stores/authStore"),
        import("@/hooks/useSessionValidation"),
      ]);

      // Surface the SessionExpiredModal first so the user sees *why* they're
      // being kicked out, then perform logout. The modal owns the redirect
      // (auto-redirect 5 s countdown), so we no longer force window.location
      // from here.
      triggerSessionExpired();
      await useAuthStore.getState().logout();
    } catch (logoutError) {
      console.error("[UNIFIED_HTTP] Erro durante logout por 401:", logoutError);
      window.location.href = "/auth/login";
    } finally {
      this.isRetrying = false;
      setTimeout(() => {
        this.isLoggingOut = false;
      }, 2000);
    }

    return Promise.reject(error);
  }

  private async handle403Error(error: any): Promise<any> {
    const errorData = error.response?.data || {};
    const errorMsg = errorData.message || "";
    const excType = errorData.exc_type || errorData.exception || "";

    if (excType === "PermissionError" || excType.includes("PermissionError")) {
      // Heuristic: short generic messages / "Guest" / "not permitted" → session expiry,
      // otherwise it's a real permission denial on a specific resource.
      const isSessionExpiry =
        errorMsg.includes("You are not permitted") ||
        errorMsg.includes("Guest") ||
        errorMsg.includes("not authenticated") ||
        (errorMsg && !errorMsg.includes(":") && errorMsg.length < 100);

      if (isSessionExpiry) {
        console.warn(
          "[UNIFIED_HTTP] 403 Forbidden — session expiry detected (PermissionError)",
        );

        if (this.isLoggingOut) {
          return Promise.reject(error);
        }

        this.isLoggingOut = true;

        try {
          sessionStorage.setItem(
            "login_error_message",
            "Sua sessão foi invalidada. Por favor, faça login novamente.",
          );

          const [{ useAuthStore }, { triggerSessionExpired }] =
            await Promise.all([
              import("@/stores/authStore"),
              import("@/hooks/useSessionValidation"),
            ]);

          triggerSessionExpired({
            type: "permission_error",
            message:
              t("auth.sessionInvalidated"),
          });
          await useAuthStore.getState().logout();
        } catch (logoutError) {
          console.error(
            "[UNIFIED_HTTP] Erro durante logout por session expiry:",
            logoutError,
          );
          window.location.href = "/auth/login";
        } finally {
          setTimeout(() => {
            this.isLoggingOut = false;
          }, 2000);
        }

        return Promise.reject(error);
      }

      // Real permission denied — bubble up with marker flag so callers can
      // skip duplicate toasts.
      console.error(
        "[UNIFIED_HTTP] 403 Forbidden — permission denied (PermissionError):",
        errorMsg,
      );

      const permissionError = new Error(errorMsg);
      (permissionError as any).isPermissionDenied = true;
      (permissionError as any).status = 403;
      return Promise.reject(permissionError);
    }

    console.error(
      "[UNIFIED_HTTP] 403 Forbidden — access denied (not PermissionError):",
      errorMsg,
    );
    return Promise.reject(error);
  }

  private buildCacheKey(endpoint: string, params?: any): string {
    return `${endpoint}:${JSON.stringify(params || {})}`;
  }

  private getCachedResponse(key: string): any | null {
    const cached = this.cacheMap.get(key);
    if (!cached) return null;
    if (Date.now() - cached.timestamp > cached.ttl) {
      this.cacheMap.delete(key);
      return null;
    }
    return cached.data;
  }

  private setCacheResponse(key: string, data: any, ttl: number): void {
    this.cacheMap.set(key, { data, timestamp: Date.now(), ttl });
  }

  private invalidateCache(endpoint: string): void {
    for (const [key] of this.cacheMap) {
      if (key.startsWith(endpoint)) {
        this.cacheMap.delete(key);
      }
    }
  }

  /**
   * Frappe returns { data: {...} } for single-doc responses and { data: [...] }
   * for list responses. We unwrap only the object case so list shapes stay intact.
   */
  private unwrapFrappeResponse(data: any): any {
    if (data === null || data === undefined) return data;
    if (
      typeof data === "object" &&
      "data" in data &&
      typeof data.data === "object" &&
      !Array.isArray(data.data)
    ) {
      return data.data;
    }
    return data;
  }

  async get<T = any>(endpoint: string, config?: HttpClientConfig): Promise<T> {
    this.initializeAxios();

    const finalConfig: HttpClientConfig = { ...(config || {}) };
    const cacheKey = this.buildCacheKey(endpoint, finalConfig.params);

    const shouldSkipCache =
      finalConfig.skipCache ||
      this.ENDPOINTS_NO_CACHE.some((e) => endpoint.includes(e));

    if (!shouldSkipCache) {
      const cached = this.getCachedResponse(cacheKey);
      if (cached !== null) {
        return cached as T;
      }
    }

    try {
      finalConfig.headers = { ...finalConfig.headers };
      const response = await this.axiosInstance!.get<T>(endpoint, finalConfig);
      const unwrappedData = this.unwrapFrappeResponse(response.data);
      const cacheTtl = finalConfig.cacheTtl || this.CACHE_TTL_DEFAULT;
      this.setCacheResponse(cacheKey, unwrappedData, cacheTtl);
      return unwrappedData as T;
    } catch (error) {
      console.error(`[UNIFIED_HTTP] GET Error: ${endpoint}`, error);
      throw error;
    }
  }

  async post<T = any>(
    endpoint: string,
    data?: any,
    config?: HttpClientConfig,
  ): Promise<T> {
    this.initializeAxios();
    const finalConfig: HttpClientConfig = { ...(config || {}) };

    try {
      finalConfig.headers = {
        ...finalConfig.headers,
        "Content-Type": "application/json",
      };
      const response = await this.axiosInstance!.post<T>(
        endpoint,
        data,
        finalConfig,
      );
      this.invalidateCache(endpoint);
      return response.data;
    } catch (error) {
      console.error(`[UNIFIED_HTTP] POST Error: ${endpoint}`, error);
      throw error;
    }
  }

  async put<T = any>(
    endpoint: string,
    data?: any,
    config?: HttpClientConfig,
  ): Promise<T> {
    this.initializeAxios();
    const finalConfig: HttpClientConfig = { ...(config || {}) };

    try {
      finalConfig.headers = {
        ...finalConfig.headers,
        "Content-Type": "application/json",
      };
      const response = await this.axiosInstance!.put<T>(
        endpoint,
        data,
        finalConfig,
      );
      this.invalidateCache(endpoint);
      return response.data;
    } catch (error) {
      console.error(`[UNIFIED_HTTP] PUT Error: ${endpoint}`, error);
      throw error;
    }
  }

  async patch<T = any>(
    endpoint: string,
    data?: any,
    config?: HttpClientConfig,
  ): Promise<T> {
    this.initializeAxios();
    const finalConfig: HttpClientConfig = { ...(config || {}) };

    try {
      finalConfig.headers = {
        ...finalConfig.headers,
        "Content-Type": "application/json",
      };
      const response = await this.axiosInstance!.patch<T>(
        endpoint,
        data,
        finalConfig,
      );
      this.invalidateCache(endpoint);
      return response.data;
    } catch (error) {
      console.error(`[UNIFIED_HTTP] PATCH Error: ${endpoint}`, error);
      throw error;
    }
  }

  /**
   * Frappe DELETE responses are typically empty (null or {}). Status 200/202/204
   * all mean success. We still unwrap in case Frappe wraps in { data: {...} }.
   */
  async delete<T = any>(
    endpoint: string,
    config?: HttpClientConfig,
  ): Promise<T> {
    this.initializeAxios();
    const finalConfig: HttpClientConfig = { ...(config || {}) };

    try {
      finalConfig.headers = { ...finalConfig.headers };
      const response = await this.axiosInstance!.delete<T>(
        endpoint,
        finalConfig,
      );
      this.invalidateCache(endpoint);
      return this.unwrapFrappeResponse(response.data) as T;
    } catch (error) {
      console.error(`[UNIFIED_HTTP] DELETE Error: ${endpoint}`, error);
      throw error;
    }
  }

  invalidateCacheByPrefix(prefix: string): void {
    for (const [key] of this.cacheMap) {
      if (key.includes(prefix)) {
        this.cacheMap.delete(key);
      }
    }
  }

  clearCache(): void {
    this.cacheMap.clear();
  }

  getCacheStats(): { size: number; entries: string[] } {
    return {
      size: this.cacheMap.size,
      entries: Array.from(this.cacheMap.keys()),
    };
  }
}

export const httpClient = new UnifiedHttpClient();
export type { HttpClientConfig };
